/**
 * 角色定義與顯示名稱
 * 
 * 這是系統中所有角色的 Single Source of Truth
 * 所有角色判斷都應該參考這個檔案
 * 
 * @module Roles
 */

/**
 * 系統角色類型
 */
export type AdminRole = 'owner' | 'super_admin' | 'store_manager' | 'staff' | 'part_time';

/**
 * 角色顯示名稱映射
 */
export const roleNames: Record<AdminRole, string> = {
  owner: '全知者',
  super_admin: '總管理',
  store_manager: '店長',
  staff: '員工',
  part_time: '工讀生',
};

/**
 * 角色層級（數字越大權限越高）
 * 
 * 注意：這個層級僅用於基本比較，不代表完整的權限關係
 * 實際權限應該查詢 permissions.ts 的權限矩陣
 */
export const roleLevel: Record<AdminRole, number> = {
  owner: 100,
  super_admin: 80,
  store_manager: 60,
  staff: 40,
  part_time: 20,
};

/**
 * 檢查角色是否有效
 */
export function isValidRole(role: string): role is AdminRole {
  return ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'].includes(role);
}

/**
 * 取得角色顯示名稱
 */
export function getRoleName(role: AdminRole): string {
  return roleNames[role] || '未知角色';
}

/**
 * 比較兩個角色的層級
 * @returns true 如果 role1 的層級 >= role2
 */
export function hasHigherOrEqualRole(role1: AdminRole, role2: AdminRole): boolean {
  return roleLevel[role1] >= roleLevel[role2];
}

/**
 * 取得指定角色可以管理的角色清單
 * 
 * 規則：
 * - owner: 可管理所有角色（owner, super_admin, store_manager, staff, part_time）
 * - super_admin: 只能管理 store_manager, staff, part_time（不能管理 owner 和 super_admin）
 * - 其他角色: 無管理權限
 * 
 * @param callerRole - 呼叫者角色
 * @returns 可管理的角色清單
 */
export function getManageableRoles(callerRole: AdminRole): AdminRole[] {
  switch (callerRole) {
    case 'owner':
      return ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'];

    case 'super_admin':
      return ['super_admin', 'store_manager', 'staff', 'part_time'];

    case 'store_manager':
      return ['staff', 'part_time'];

    default:
      return [];
  }
}

/**
 * 檢查呼叫者是否可以管理目標角色
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以管理
 */
export function canManageRole(callerRole: AdminRole, targetRole: AdminRole): boolean {
  const manageableRoles = getManageableRoles(callerRole);
  return manageableRoles.includes(targetRole);
}

/**
 * 檢查呼叫者是否可以編輯目標管理員
 * 
 * 規則：
 * - owner 可編輯所有人
 * - super_admin 只能編輯 store_manager, staff, part_time（不可編輯 owner）
 * - store_manager 不可編輯任何人
 * - 其他角色無編輯權限
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以編輯
 */
export function canEditAdmin(callerRole: AdminRole, targetRole: AdminRole): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'super_admin') return targetRole !== 'owner';
  return false;
}

/**
 * 檢查呼叫者是否可以刪除目標管理員
 * 
 * 規則：
 * - 只有 owner 可刪除
 * - owner 本身不可被刪除（需在頁面層額外檢查）
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以刪除
 */
export function canDeleteAdmin(callerRole: AdminRole, targetRole: AdminRole): boolean {
  // 只有 owner 可刪除
  if (callerRole !== 'owner') return false;
  
  // owner 可刪除所有角色（但在頁面層應阻止刪除自己）
  return true;
}

/**
 * 檢查呼叫者是否可以重設目標管理員密碼
 * 
 * 規則：
 * - owner 可重設所有人密碼
 * - super_admin 只能重設 store_manager, staff, part_time 密碼
 * - 其他角色無重設權限
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以重設密碼
 */
export function canResetPassword(callerRole: AdminRole, targetRole: AdminRole): boolean {
  return canManageRole(callerRole, targetRole);
}